/** Importaciones Angular - Ionic */
import { ScrollingModule } from '@angular/cdk/scrolling'
import { registerLocaleData } from '@angular/common'
import localeEn from '@angular/common/locales/en'
import localePy from '@angular/common/locales/es'
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { Router, RouteReuseStrategy } from '@angular/router'
import { environment } from '../environments/environment'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'

import { IonicModule, IonicRouteStrategy, Platform } from '@ionic/angular'
import { IonicStorageModule, Storage } from '@ionic/storage'

/** Plugins */
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx'
import { CallNumber } from '@awesome-cordova-plugins/call-number/ngx'
import { CameraPreview } from '@awesome-cordova-plugins/camera-preview/ngx'
import { File } from '@awesome-cordova-plugins/file/ngx'
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx'
import { Network } from '@awesome-cordova-plugins/network/ngx'
import { SocialSharing } from '@awesome-cordova-plugins/social-sharing/ngx'

/** Services */
import { ServiceWorkerModule } from '@angular/service-worker'
import { NetworkService } from '../app/shared/services/network.service'
import { TransferDataService } from './private/shared/services/transfer-data.service'
import { AnalyticsService } from './shared/services/analytics.service'
import { AppUpdateService } from './shared/services/app-update.service'
import { AuthService } from './shared/services/auth-service'
import { EncryptionService } from './shared/services/encryption/encryption.service'
import { FirebaseService } from './shared/services/firebase.service'
import { MonitoringService } from './shared/services/monitoring/monitoring.service'
import { OverlayService } from './shared/services/overlays/overlay.service'
import { PopoverDropdownService } from './shared/services/popover/popover-dropdown.service'
import { Premia2Service } from './shared/services/premia2.service'
import { SharedServiceService } from './shared/services/shared-service.service'
import { SmsServiceService } from './shared/services/sms-service.service'
import { TicketEntregaService } from './shared/services/ticket-entrega.service'
import { StorageData } from './shared/services/utilities/storage-data.class'

/** Firebase */
import { provideFirebaseApp } from '@angular/fire/app'
import { provideFirestore } from '@angular/fire/firestore'
import { getApp, initializeApp } from '@firebase/app'
import '@firebase/firestore'
import { getFirestore } from '@firebase/firestore'


/** Miselaneos */
import { TranslateLoader, TranslateModule } from '@ngx-translate/core'
import { NgxChartsModule } from '@swimlane/ngx-charts'
import { GaugeModule } from 'angular-gauge'
import { RECAPTCHA_SETTINGS, RecaptchaSettings, RecaptchaV3Module } from 'ng-recaptcha'
import { CustomTranslateLoader } from './shared/services/custom-translate/custom-translate.service'

/** NGRX */
import { EffectsModule } from '@ngrx/effects'
import { StoreModule } from '@ngrx/store'
import { extModules } from '../build-specifics'

import { AppReducers } from './store/app-reducers'
import { EffectsArray } from './store/effects'

/** Modales, Modulos, Pipes */
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http'
import { ModalConfirmPageModule } from './private/shared/components/modal-confirm/modal-confirm.module'
import { ModalConfirmPage } from './private/shared/components/modal-confirm/modal-confirm.page'
import { TerminosPageModule } from './private/terminos/terminos.module'
import { TerminosPage } from './private/terminos/terminos.page'
import { UpdateAppPageModule } from './public/update-app/update-app.module'
import { UpdateAppPage } from './public/update-app/update-app.page'
import { HamburgerMenuComponent } from './shared/components/hamburger-menu/hamburger-menu.component'
import { OverlayPageModule } from './shared/components/overlay/overlay.module'
import { OverlayPage } from './shared/components/overlay/overlay.page'
import { ModalsModule } from './shared/modals/modals.module'

import { MantenimientoScreanPageModule } from './shared/components/mantenimiento-screan/mantenimiento-screan.module'
import { MantenimientoScreanPage } from './shared/components/mantenimiento-screan/mantenimiento-screan.page'
import { CheckIconPipe } from './shared/pipes/check-icon/check-icon.pipe'
import { HtmlSeguroPipe } from './shared/pipes/html-seguro.pipe'
import { SummaryPricePipe } from './shared/pipes/summary-price/summary-price.pipe'
import { VideoPipe } from './shared/pipes/video/video.pipe'

/** Sentry */
import * as Sentry from "@sentry/angular-ivy"
import { HttpErrorRulesInterceptor } from './shared/interceptors/http-error-rules-interceptor'
import { MicroservicesHttpAuthInterceptor } from './shared/interceptors/microservices-http-auth-interceptor'
import { AssetsUrlPipe } from './shared/pipes/assets-url/assets-url.pipe'

const globalSettings: RecaptchaSettings = { siteKey: environment.Recaptcha };

registerLocaleData(localePy, 'es');
registerLocaleData(localeEn, 'en');

export function initEncryptedStorageFactory(storage: Storage, encryption: EncryptionService): () => Promise<void> {
  return async () => {
    await storage.ready();
    StorageData.init(storage, encryption);
  };
}

export function initializeFirebaseFactory(firebaseService: FirebaseService) {
  return () => firebaseService.initialize();
}

export function initializeMonitoringFactory(monitoringService: MonitoringService) {
  return () => monitoringService.initialize();
}

@NgModule({
  declarations: [
    AppComponent
    , HamburgerMenuComponent
  ],
  entryComponents: [
    UpdateAppPage
    , OverlayPage
    , TerminosPage
    , ModalConfirmPage
    , MantenimientoScreanPage
  ],
  imports: [
    BrowserModule
    , ScrollingModule
    , IonicModule.forRoot({
      hardwareBackButton: true,
      swipeBackEnabled: false,
      innerHTMLTemplatesEnabled: true,
    })
    , BrowserAnimationsModule
    , AppRoutingModule

    , provideFirebaseApp(() => initializeApp(environment.firebaseConfig))
    , provideFirestore(() => getFirestore(getApp()))

    , IonicStorageModule.forRoot({
      name: '__geppdb',
      driverOrder: ['indexeddb', 'sqlite', 'websql']
    })
    , GaugeModule.forRoot()
    , ServiceWorkerModule.register('combined-sw.js', { enabled: environment.production })

    , HttpClientModule
    , NgxChartsModule

    , StoreModule.forRoot(AppReducers)
    , EffectsModule.forRoot(EffectsArray)
    , extModules

    , TranslateModule.forRoot({
      defaultLanguage: 'es',
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
        deps: [HttpClient]
      }
    })

    , RecaptchaV3Module

    , ModalsModule
    , UpdateAppPageModule
    , OverlayPageModule
    , TerminosPageModule
    , ModalConfirmPageModule
    , MantenimientoScreanPageModule
  ],
  providers: [
    // Interceptos
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorRulesInterceptor, multi: true }
    , { provide: HTTP_INTERCEPTORS, useClass: MicroservicesHttpAuthInterceptor, multi: true }

    , { provide: RouteReuseStrategy, useClass: IonicRouteStrategy }
    , { provide: RECAPTCHA_SETTINGS, useValue: globalSettings }
    , { provide: LOCALE_ID, useValue: 'en' }

    , InAppBrowser
    , Platform
    , Network
    , CallNumber
    , CameraPreview
    , AndroidPermissions
    , File


    , HtmlSeguroPipe
    , VideoPipe
    , CheckIconPipe
    , SummaryPricePipe
    , AssetsUrlPipe

    , SocialSharing
    , AppUpdateService
    , SharedServiceService
    , TransferDataService
    , AuthService
    , AnalyticsService
    , NetworkService
    , TicketEntregaService
    , OverlayService
    , Premia2Service
    , PopoverDropdownService
    , SmsServiceService

    // Sentry
    , { provide: ErrorHandler, useValue: Sentry.createErrorHandler() }
    , { provide: Sentry.TraceService, deps: [Router] }
    , { provide: APP_INITIALIZER, useFactory: () => () => { }, multi: true, deps: [Sentry.TraceService] }
    , { provide: APP_INITIALIZER, useFactory: initEncryptedStorageFactory, deps: [Storage, EncryptionService], multi: true }
    , { provide: APP_INITIALIZER, useFactory: initializeFirebaseFactory, deps: [FirebaseService], multi: true }
    , { provide: APP_INITIALIZER, useFactory: initializeMonitoringFactory, deps: [MonitoringService], multi: true }
  ],
  exports: [HamburgerMenuComponent],
  bootstrap: [AppComponent]
})

export class AppModule { }
